import { useEffect, useState } from 'react';

import getCustomer from '@/shared/api/customers/getCustomer';
import getOrder from '@/shared/api/order/getOrder';
import cancelOrder from '@/shared/api/orders/cancelOrder';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Customer from '@/shared/types/customer.type';
import DialogName from '@/shared/types/dialogName.enum';
import Order from '@/shared/types/order/order.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type Args = {
  orderId: string;
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
};

const useOrderEditOperations = ({
  orderId,
  onUpdateDialogState,
  onUpdateNotificationBar,
}: Args) => {
  const redirectLogin = useRedirectLogin();

  const [order, setOrder] = useState<Order | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [refetchOrder, setRefetchOrder] = useState(false);

  useEffect(() => {
    const fetchCustomer = async (order: Order) => {
      const customerResponse = await getCustomer(
        order.in_customer_id,
        redirectLogin,
      );

      if (customerResponse.success) {
        setCustomer(customerResponse.data[0]);
      }
    };

    const fetchOrder = async () => {
      const response = await getOrder(orderId, redirectLogin);

      if (response.success) {
        setOrder(response.data[0]);
        await fetchCustomer(response.data[0]);
      }
    };

    void fetchOrder();
  }, [orderId, refetchOrder, redirectLogin]);

  const handleCancelOrder = async (id: string, isCloseWithRefund: boolean) => {
    onUpdateDialogState(DialogName.CancelOrder, { loading: true });

    const response = await cancelOrder({
      orderId: id,
      body: {
        refund_order: isCloseWithRefund,
      },
      onUnauthorized: redirectLogin,
    });

    onUpdateDialogState(DialogName.CancelOrder, { loading: false });

    if (response.success) {
      setOrder(response.data[0]);
      onUpdateDialogState(DialogName.CancelOrder, { open: false });
      onUpdateNotificationBar({
        open: true,
        message: 'The order was successfully cancelled',
      });
    } else {
      onUpdateDialogState(DialogName.CancelOrder, { errors: [response.msg] });
    }
  };

  return {
    order,
    customer,
    setRefetchOrder,
    handleCancelOrder,
  };
};

export default useOrderEditOperations;
